import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, UserPlus, Settings2, CreditCard, CalendarPlus, Tags, Lock,
  Rocket, CheckSquare, ScanLine, BarChart3, ShieldCheck, EyeOff, Ticket,
  Gift, XCircle, RefreshCcw, AlertCircle, FileText, Users, Key, Percent, Clock,
  HelpCircle, Sparkles, Crown,
} from 'lucide-react';
import { Button } from '../components/ui/button';

/* ───────────────────────── Primitives ───────────────────────── */

function SectionHeader({ eyebrow, title, description, icon: Icon }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-[#D4AF37]" />}
        <div className="text-[10px] tracking-[0.35em] text-[#D4AF37] uppercase font-semibold">
          {eyebrow}
        </div>
      </div>
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#F3EEE7]">{title}</h2>
      {description && (
        <p className="text-sm text-[#B9B1A6] mt-2 leading-relaxed max-w-2xl">{description}</p>
      )}
    </div>
  );
}

function ConceptCard({ icon: Icon, title, children, testId }) {
  return (
    <div
      className="border border-[#1A1A1A] rounded-sm bg-[#0A0A0A] p-4 hover:border-[#D4AF37]/30 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />}
        <h3 className="text-sm font-bold text-[#F3EEE7] tracking-tight">{title}</h3>
      </div>
      <div className="text-[13px] text-[#B9B1A6] leading-relaxed">{children}</div>
    </div>
  );
}

function StepCard({ n, icon: Icon, title, children, testId }) {
  return (
    <div
      className="relative border border-[#1A1A1A] rounded-sm bg-[#0A0A0A] p-4 hover:border-[#D4AF37]/30 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
          {Icon ? <Icon className="w-4 h-4 text-[#D4AF37]" /> : <span className="text-xs font-bold text-[#D4AF37]">{n}</span>}
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] font-bold text-[#777] tracking-wider">STEP {n}</span>
          </div>
          <h3 className="text-sm font-bold text-[#F3EEE7] tracking-tight mt-0.5">{title}</h3>
          <p className="text-[13px] text-[#B9B1A6] leading-relaxed mt-1">{children}</p>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Data ───────────────────────── */

const HOST_FLOW = [
  { icon: UserPlus,     title: 'Create Account',       text: 'Sign up with email or Google. Free — hosting requires a short application.' },
  { icon: Settings2,    title: 'Set Defaults',         text: 'Save your organizer logo, staff-access window, and reusable preferences once.' },
  { icon: CreditCard,   title: 'Add Payment Methods',  text: 'Zelle, Venmo, CashApp, or any direct-transfer handle. Guests use these to pay.' },
  { icon: CalendarPlus, title: 'Create Event',         text: 'Add title, date, location, flyer, and pricing. Duplicate past events to save time.' },
  { icon: Tags,         title: 'Set Pass Tiers',       text: 'General, VIP, comps, add-ons. Tiers can be active, hidden, or code-gated.' },
  { icon: Lock,         title: 'Choose Access Rules',  text: 'Public or private listing. Auto-release or require host approval for each pass.' },
  { icon: Rocket,       title: 'Publish Event',        text: 'Share the public page link or keep it private and send directly to your list.' },
  { icon: CheckSquare,  title: 'Approve Guests',       text: 'Pending requests surface in your Approvals Bell — bulk-approve or review one-by-one.' },
  { icon: ScanLine,     title: 'Check-In',             text: 'Scan QR codes, search by name, or let guests self-check-in on arrival.' },
  { icon: BarChart3,    title: 'Review Reports',       text: 'Pass counts, attendance, revenue, and payment breakdowns — exportable.' },
];

const CONCEPTS = [
  { icon: Clock,      title: 'Pending vs Granted',              body: <><b className="text-[#FFB800]">Pending</b> passes are requested but awaiting host approval — the guest cannot enter yet. <b className="text-emerald-400">Granted</b> passes are approved and valid for check-in.</>, testId: 'concept-pending-granted' },
  { icon: ShieldCheck,title: 'Manual Approval vs Auto Release',body: <><b>Manual approval</b> puts every request in your queue for review. <b>Auto release</b> grants access immediately — best for public, general-admission events.</>, testId: 'concept-approval-release' },
  { icon: EyeOff,     title: 'Hidden Address vs Reveal Later',  body: <><b>Hidden</b> never exposes the location via the platform — you communicate it privately. <b>Reveal Later</b> keeps it masked publicly, then surfaces it automatically to approved guests before the event.</>, testId: 'concept-hidden-reveal' },
  { icon: Tags,       title: 'Pass Tiers',                      body: <>A pass tier is a purchasable type (General, VIP, Couples, Add-on, etc.). Each tier has its own price, capacity, and release rule. Save tiers as templates for future events.</>, testId: 'concept-pass-tiers' },
  { icon: Key,        title: 'Access Codes vs Discount Codes',  body: <><b>Access codes</b> unlock a hidden tier entirely (invite-only). <b>Discount codes</b> reduce the price of a visible tier. They serve very different purposes — do not mix them.</>, testId: 'concept-codes' },
  { icon: Gift,       title: 'Comp vs Paid',                    body: <><b>Comp</b> passes are issued by the host at no cost (VIPs, crew, press). <b>Paid</b> passes reflect guest-confirmed payment via your listed methods. Both are reported separately.</>, testId: 'concept-comp-paid' },
  { icon: RefreshCcw, title: 'Revoked vs Replaced',             body: <><b>Revoked</b> permanently invalidates a pass (no longer valid for entry). <b>Replaced</b> revokes the original AND issues a fresh pass to the same guest with the same tier — preserves history without re-consuming capacity.</>, testId: 'concept-revoked-replaced' },
];

/* ───────────────────────── Page ───────────────────────── */

export default function Guide() {
  const navigate = useNavigate();
  const location = useLocation();

  // Support deep links like /guide#hidden-address
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
      }
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-[#050505] text-[#EAE7E1]" data-testid="guide-page">
      {/* Header */}
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))}
            variant="ghost"
            className="mb-4 -ml-2 text-[#AFAFAF] hover:text-[#EAE7E1]"
            data-testid="guide-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.35em] text-[#D4AF37] uppercase font-semibold">VelvetGate</div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#F3EEE7]" data-testid="guide-title">VelvetGate Guide</h1>
            </div>
          </div>
          <p className="mt-2 text-base text-[#AFAFAF]" data-testid="guide-subtitle">Run your events with precision.</p>
        </div>
      </div>

      {/* Sticky mini-nav */}
      <nav
        className="sticky top-0 z-10 bg-[#050505]/95 backdrop-blur border-b border-[#1A1A1A]"
        aria-label="Guide sections"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto">
          <ul className="flex items-center gap-4 sm:gap-6 text-xs text-[#777] py-3 whitespace-nowrap" data-testid="guide-mini-nav">
            {[
              ['start-here',        'Start Here'],
              ['host-setup',        'Host Setup'],
              ['key-concepts',      'Key Concepts'],
              ['creating-an-event', 'Creating an Event'],
              ['running-event',     'Running Your Event'],
              ['after-event',       'After the Event'],
              ['plans-access',      'Plans & Access'],
            ].map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="hover:text-[#D4AF37] transition-colors" data-testid={`guide-jump-${id}`}>
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-16">

        {/* SECTION A — Start Here */}
        <section id="start-here" data-testid="guide-section-start-here" className="scroll-mt-20">
          <SectionHeader
            eyebrow="Section A"
            title="Start Here"
            description="VelvetGate is a private event platform for curated experiences — dinner parties, underground shows, salons, private salons, and selective gatherings. Built for hosts who care about who walks through the door."
            icon={BookOpen}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ConceptCard icon={Users}      title="Who it's for"     testId="start-audience">Independent hosts, collectives, and venues running invite-driven or approval-based events.</ConceptCard>
            <ConceptCard icon={ShieldCheck} title="Core idea"         testId="start-idea">Control, privacy, and curated access — the host decides who gets in, the platform handles the logistics.</ConceptCard>
            <ConceptCard icon={Rocket}     title="What you get"      testId="start-offering">Hosted event pages, approvals, check-in, payment proof capture, pass revocation/replacement, and reporting.</ConceptCard>
          </div>
        </section>

        {/* SECTION B — Host Setup Flow */}
        <section id="host-setup" data-testid="guide-section-host-setup" className="scroll-mt-20">
          <SectionHeader
            eyebrow="Section B"
            title="Host Setup Flow"
            description="From account creation to your first live event, in order."
            icon={Rocket}
          />
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="host-flow-grid">
            {HOST_FLOW.map((s, i) => (
              <li key={s.title}>
                <StepCard n={i + 1} icon={s.icon} title={s.title} testId={`flow-step-${i + 1}`}>
                  {s.text}
                </StepCard>
              </li>
            ))}
          </ol>
        </section>

        {/* SECTION C — Key Concepts */}
        <section id="key-concepts" data-testid="guide-section-key-concepts" className="scroll-mt-20">
          <SectionHeader
            eyebrow="Section C"
            title="Key Concepts"
            description="The vocabulary and logic behind how VelvetGate works."
            icon={HelpCircle}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Deep-link anchors so contextual 'What does this mean?' links land on the right card */}
            <div id="hidden-address" className="scroll-mt-20"><ConceptCard icon={CONCEPTS[2].icon} title={CONCEPTS[2].title} testId={CONCEPTS[2].testId}>{CONCEPTS[2].body}</ConceptCard></div>
            <div id="pass-tiers"     className="scroll-mt-20"><ConceptCard icon={CONCEPTS[3].icon} title={CONCEPTS[3].title} testId={CONCEPTS[3].testId}>{CONCEPTS[3].body}</ConceptCard></div>
            <ConceptCard icon={CONCEPTS[0].icon} title={CONCEPTS[0].title} testId={CONCEPTS[0].testId}>{CONCEPTS[0].body}</ConceptCard>
            <ConceptCard icon={CONCEPTS[1].icon} title={CONCEPTS[1].title} testId={CONCEPTS[1].testId}>{CONCEPTS[1].body}</ConceptCard>
            <ConceptCard icon={CONCEPTS[4].icon} title={CONCEPTS[4].title} testId={CONCEPTS[4].testId}>{CONCEPTS[4].body}</ConceptCard>
            <ConceptCard icon={CONCEPTS[5].icon} title={CONCEPTS[5].title} testId={CONCEPTS[5].testId}>{CONCEPTS[5].body}</ConceptCard>
            <ConceptCard icon={CONCEPTS[6].icon} title={CONCEPTS[6].title} testId={CONCEPTS[6].testId}>{CONCEPTS[6].body}</ConceptCard>
          </div>
        </section>

        {/* SECTION D — Creating an Event */}
        <section id="creating-an-event" data-testid="guide-section-creating" className="scroll-mt-20">
          <SectionHeader
            eyebrow="Section D"
            title="Creating an Event"
            description="Practical guidance for each decision point."
            icon={CalendarPlus}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ConceptCard icon={Tags}       title="Setting Tiers"     testId="creating-tiers">Start with one tier if you're unsure. Add VIP or add-ons only when they map to something real (seating, merch, early entry). Hidden tiers require an access code to appear.</ConceptCard>
            <ConceptCard icon={Percent}    title="Pricing"           testId="creating-pricing">Price at what your audience will pay today, not what you wish they would. Comps don't reduce your paid revenue — they're tracked separately in Reports.</ConceptCard>
            <ConceptCard icon={ShieldCheck}title="Approval Style"    testId="creating-approval">Use <b>manual approval</b> for private or high-touch events. Use <b>auto release</b> for open-door events where you want zero friction.</ConceptCard>
            <ConceptCard icon={CreditCard} title="Payments"          testId="creating-payments">Add all the methods you accept upfront. Guests pick one and enter a reference or upload a screenshot as proof — you verify before approving.</ConceptCard>
            <ConceptCard icon={Lock}       title="Privacy"           testId="creating-privacy">Unlisted events are only reachable by direct link. Hidden or Reveal-Later locations keep your address off the public page entirely.</ConceptCard>
            <ConceptCard icon={Key}        title="Access Codes"      testId="creating-access-codes">Use access codes for invite-only tiers — guests who don't know the code never see the tier exists.</ConceptCard>
          </div>
        </section>

        {/* SECTION E — Running Your Event */}
        <section id="running-event" data-testid="guide-section-running" className="scroll-mt-20">
          <SectionHeader
            eyebrow="Section E"
            title="Running Your Event"
            description="What to do once the door opens."
            icon={ScanLine}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ConceptCard icon={CheckSquare} title="Approvals"        testId="running-approvals">The Approvals Bell on your Dashboard surfaces pending requests. Bulk-approve the obvious ones; open a request to see the guest's details and payment reference.</ConceptCard>
            <ConceptCard icon={AlertCircle} title="Handling Mistakes"testId="running-mistakes">Wrong tier? <b>Revoke &amp; Replace</b> issues a fresh pass in the correct tier without re-consuming capacity. No-show abuse? <b>Revoke</b> invalidates the pass; the guest sees it marked void.</ConceptCard>
            <ConceptCard icon={ScanLine}    title="Check-In Basics"  testId="running-checkin">Scan the QR with your phone, search by name or email from the Check-In page, or share a self-check-in link with your door team. Every scan is logged with who scanned it.</ConceptCard>
          </div>
        </section>

        {/* SECTION F — After the Event */}
        <section id="after-event" data-testid="guide-section-after" className="scroll-mt-20">
          <SectionHeader
            eyebrow="Section F"
            title="After the Event"
            description="What the numbers mean."
            icon={FileText}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ConceptCard icon={BarChart3} title="Reports Overview" testId="after-reports">The Reports page aggregates every pass across your events. Filter by date, event, or status. Export as CSV, PDF, or XLSX — the numbers are computed from the same source as the dashboard.</ConceptCard>
            <ConceptCard icon={Ticket}    title="Total Passes"     testId="after-total-passes">Every pass issued — granted, pending, used, or revoked. It's the top-of-funnel count, not your attendance number.</ConceptCard>
            <ConceptCard icon={Users}     title="Attendees"        testId="after-attendees">Sum of <i>attendees_count</i> across passes (a pass may cover 1 or more people). Closer to your real headcount than "passes".</ConceptCard>
            <ConceptCard icon={CheckSquare} title="Checked-In %"    testId="after-checkin-pct">How many attendees actually came through the door. Compare across events to understand your no-show rate.</ConceptCard>
            <ConceptCard icon={XCircle}    title="Revoked"          testId="after-revoked">Passes you invalidated (duplicate purchase, no-show abuse, wrong tier). Tracked separately so they don't inflate attendance.</ConceptCard>
            <ConceptCard icon={CreditCard} title="Reported Gross"   testId="after-gross">Host-entered amount paid across confirmed paid passes. Not a payment-processor figure — it's what you reported when approving each pass.</ConceptCard>
          </div>
        </section>

        {/* SECTION G — Plans & Access */}
        <section id="plans-access" data-testid="guide-section-plans-access" className="scroll-mt-20">
          <SectionHeader
            eyebrow="Section G"
            title="Plans & Access"
            description="VelvetGate is free to start. Advanced features unlock as your hosting grows — or instantly via a VelvetGate access code."
            icon={Crown}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ConceptCard icon={Rocket}  title="Starter — Run Your Event"         testId="plan-starter">Everything you need to launch: create events, sell tickets, approve guests, and check in at the door. The default for every account.</ConceptCard>
            <ConceptCard icon={Sparkles} title="Pro — Insights & Growth"           testId="plan-pro">Unlock <b>advanced report filters</b>, Guest Attendance, expanded XLSX exports, Post Event Insights, saved snapshots, access codes, and discount codes. Designed for hosts running recurring events.</ConceptCard>
            <ConceptCard icon={Crown}   title="Premium — Branding & Control"       testId="plan-premium">Everything in Pro, plus <b>custom branding</b> on guest-facing pages (your logo on tickets, emails, and event pages replacing the VelvetGate default) and identity ownership.</ConceptCard>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <ConceptCard icon={Gift}         title="Trials"                 testId="plan-trials">Promotional access granted by VelvetGate via a QR code or redemption link. Trials unlock <b>all premium features</b> for a fixed window — once the window ends, your account returns to its underlying tier automatically. No billing, no surprise charges.</ConceptCard>
            <ConceptCard icon={ShieldCheck}  title="Promotional Access"   testId="plan-admin-access">Selected hosts may receive permanent or temporary full access through a VelvetGate code (partnerships, launch events, venue arrangements). Promotional access works exactly like a trial but can be permanent.</ConceptCard>
          </div>
          <div className="mt-4 text-xs text-[#777] bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-3" data-testid="plans-note">
            Have a code? Visit <Link to="/redeem" className="text-[#D4AF37] hover:underline">/redeem</Link> or scan your QR to unlock access instantly.
          </div>
        </section>

        {/* Footer CTA */}
        <div className="border-t border-[#1A1A1A] pt-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[#777]">Still have a question?</p>
            <div className="flex items-center gap-3 text-xs">
              <a href="/faq"     className="text-[#D4AF37] hover:underline" data-testid="guide-faq-link">View FAQ</a>
              <span className="text-[#2A2A2A]">·</span>
              <a href="/contact" className="text-[#D4AF37] hover:underline" data-testid="guide-contact-link">Contact VelvetGate</a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
